/**
 * CompositionLimitModal Component
 * Modal displayed when user reaches their composition storage limit
 */

import { useEffect, useRef } from 'react';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';
import { CloseButton } from './CloseButton';
import type { SubscriptionTier } from '../../types/user';

/**
 * Composition limits by subscription tier
 */
const COMPOSITION_LIMITS: Record<SubscriptionTier, number> = {
  free: 15,
  premium: 100,
  enterprise: 1000,
};

/**
 * Props for CompositionLimitModal component
 */
interface CompositionLimitModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Current number of compositions */
  currentCount: number;
  /** User's subscription tier */
  subscriptionTier: SubscriptionTier;
  /** Optional callback to navigate to My Compositions page */
  onNavigateToLibrary?: () => void;
}

/**
 * CompositionLimitModal component
 * Displays limit information and upgrade options when user reaches their composition limit
 */
export function CompositionLimitModal({
  isOpen,
  onClose,
  currentCount,
  subscriptionTier,
  onNavigateToLibrary,
}: CompositionLimitModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const limit = COMPOSITION_LIMITS[subscriptionTier];

  // Announce modal opening and trap focus
  useEffect(() => {
    if (isOpen) {
      announceToScreenReader(
        `Composition limit reached. You have ${currentCount} of ${limit} compositions saved. Please delete old compositions or upgrade your plan.`,
        'assertive'
      );
      
      // Trap focus within modal
      if (dialogRef.current) {
        const cleanup = trapFocus(dialogRef.current);
        return cleanup;
      }
    }
    return undefined;
  }, [isOpen, currentCount, limit]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="preset-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="composition-limit-dialog-title"
    >
      <div className="preset-dialog" ref={dialogRef} style={{ maxWidth: '500px' }}>
        <div className="preset-dialog-header">
          <h2 id="composition-limit-dialog-title" className="preset-dialog-title">
            Composition Limit Reached
          </h2>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close dialog"
            size="medium"
            variant="minimal"
          />
        </div>

        <div className="preset-dialog-form">
          {/* Current Status */}
          <div
            style={{
              background: 'rgba(255, 165, 0, 0.1)',
              border: '1px solid rgba(255, 165, 0, 0.3)',
              borderRadius: '6px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
              <strong>
                {currentCount} / {limit} compositions
              </strong>
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
              You've reached your {subscriptionTier} tier limit
            </div>
          </div>

          {/* Options */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3
              style={{
                fontSize: '0.85rem',
                marginBottom: '0.75rem',
                color: 'var(--color-accent)',
              }}
            >
              What would you like to do?
            </h3>

            {/* Option 1: Delete old compositions */}
            <div
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-accent)',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '0.75rem',
              }}
            >
              <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                <strong>🗑️ Delete Old Compositions</strong>
              </div>
              <div style={{ fontSize: '0.7rem', marginBottom: '0.75rem', opacity: 0.9 }}>
                Free up space by removing compositions you no longer need
              </div>
              {onNavigateToLibrary && (
                <button
                  type="button"
                  className="preset-btn preset-btn-secondary"
                  onClick={() => {
                    onNavigateToLibrary();
                    onClose();
                  }}
                  style={{ width: '100%', fontSize: '0.75rem' }}
                >
                  Go to My Compositions
                </button>
              )}
            </div>

            {/* Option 2: Upgrade (only show if not enterprise) */}
            {subscriptionTier !== 'enterprise' && (
              <div
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-accent)',
                  borderRadius: '6px',
                  padding: '1rem',
                }}
              >
                <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                  <strong>⭐ Upgrade Your Plan</strong>
                </div>
                <div style={{ fontSize: '0.7rem', marginBottom: '0.75rem', opacity: 0.9 }}>
                  {subscriptionTier === 'free'
                    ? 'Upgrade to Premium for 100 compositions'
                    : 'Upgrade to Enterprise for 1,000 compositions'}
                </div>

                {/* Upgrade tiers */}
                <div style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>
                  {subscriptionTier === 'free' && (
                    <>
                      <div style={{ marginBottom: '0.25rem' }}>
                        • <strong>Premium:</strong> 100 compositions
                      </div>
                      <div>
                        • <strong>Enterprise:</strong> 1,000 compositions
                      </div>
                    </>
                  )}
                  {subscriptionTier === 'premium' && (
                    <div>
                      • <strong>Enterprise:</strong> 1,000 compositions
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="preset-btn preset-btn-primary"
                  onClick={() => {
                    // TODO: Navigate to pricing/upgrade page
                    alert('Upgrade functionality coming soon!');
                  }}
                  style={{ width: '100%', fontSize: '0.75rem' }}
                >
                  View Upgrade Options
                </button>
              </div>
            )}
          </div>

          {/* Close button */}
          <div className="preset-dialog-actions">
            <button
              type="button"
              className="preset-btn preset-btn-secondary"
              onClick={onClose}
              style={{ width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
